// Asset URL resolver: keeps image paths portable across environments.
//
// Why: admin uploads return relative URLs like "/api/uploads/<file>.png".
// The same URL must render in any environment (preview, production, local
// dev) because the static file lives on the backend that served the
// upload. Rather than baking the host into the database, we resolve at
// render time using the current frontend's REACT_APP_BACKEND_URL.
//
// Catalog product photos live on Cloudflare Pages project bob-catalog-assets
// (too large to ship inside the main bob-website deploy). Relative
// `/products/...` paths are rewritten to that CDN in production builds.
// Legacy Emergent artifact URLs are also rewritten to the same CDN path
// once files are synced via scripts/sync-emergent-catalog-assets.py.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const PRODUCTS_CDN = (
  process.env.REACT_APP_PRODUCTS_CDN || "https://bob-catalog-assets.pages.dev"
).replace(/\/$/, "");

const EMERGENT_ARTIFACT_RE =
  /^https:\/\/customer-assets\.emergentagent\.com\/job_bob-global-shop\/artifacts\/(.+)$/i;

export function resolveAssetUrl(url) {
  if (!url) return url;
  const emergent = EMERGENT_ARTIFACT_RE.exec(url);
  if (emergent) {
    const filename = decodeURIComponent(emergent[1]);
    if (process.env.NODE_ENV === "development" && !process.env.REACT_APP_PRODUCTS_CDN) {
      return `/products/${filename}`;
    }
    return `${PRODUCTS_CDN}/products/${filename}`;
  }
  // Already absolute (https://, http://, data:, blob:) → use as-is.
  if (/^(https?:|data:|blob:)/i.test(url)) return url;
  // Backend-served paths must be routed through the API host. Both
  // `/api/uploads/` (current) and `/uploads/` (transitional legacy)
  // are normalized to the canonical /api/uploads/ form so the K8s
  // ingress correctly forwards them to the backend.
  if (url.startsWith("/api/uploads/")) {
    return `${BACKEND_URL}${url}`;
  }
  if (url.startsWith("/uploads/")) {
    return `${BACKEND_URL}/api${url}`;
  }
  if (url.startsWith("/products/")) {
    // Local CRA `yarn start` still serves public/products from the same origin.
    if (process.env.NODE_ENV === "development" && !process.env.REACT_APP_PRODUCTS_CDN) {
      return url;
    }
    return `${PRODUCTS_CDN}${url}`;
  }
  return url;
}

/**
 * Serve store photos as smaller WebP via wsrv.nl.
 * Emergent catalog PNGs are often 1–4MB; a 480px card needs ~20–40KB.
 * Returns the original URL for local/relative paths or if width is omitted.
 */
export function optimizeStoreImage(url, { width = 480, quality = 70 } = {}) {
  const resolved = resolveAssetUrl(url);
  if (!resolved || !/^https?:\/\//i.test(resolved)) return resolved;
  if (/wsrv\.nl|images\.weserv\.nl/i.test(resolved)) return resolved;
  const encoded = encodeURIComponent(resolved);
  return `https://wsrv.nl/?url=${encoded}&w=${width}&output=webp&q=${quality}`;
}
